import { useState } from "react";
import { F, D } from "../../../styles/palette";
import { Bdg } from "../../shared";
import { ISL } from "../../../data/islStages";
import PatientForm from "./PatientForm";
import Evoluciones from "./Evoluciones";
import ArchivoFotografico from "./ArchivoFotografico";
import ConsentimientoInformado from "./ConsentimientoInformado";
import PatientDashboard from "./PatientDashboard";

const SUBTABS = [
  { id: "datos", l: "Datos" },
  { id: "evol", l: "Evoluciones" },
  { id: "fotos", l: "Fotos" },
  { id: "consent", l: "Consentimiento" },
  { id: "dashboard", l: "Dashboard" },
];

export default function PatientDetail({ patient, user, updatePatient, getPatient, onBack }) {
  const [subTab, setSubTab] = useState("datos");
  const [editing, setEditing] = useState(false);
  const [pat, setPat] = useState(patient);

  const islData = pat.estadioISL != null ? ISL[pat.estadioISL] : null;
  const fullName = `${pat.primerNombre} ${pat.segundoNombre || ""} ${pat.primerApellido} ${pat.segundoApellido || ""}`.replace(/\s+/g, " ").trim();

  const handleUpdate = async (data) => {
    await updatePatient(pat.id, data);
    setPat({ ...pat, ...data });
    setEditing(false);
  };

  return (
    <div>
      <button onClick={onBack} style={{ background: "none", border: "none", color: D.ac, cursor: "pointer", fontSize: 13, fontFamily: F.b, marginBottom: 12, padding: 0 }}>
        &larr; Volver a pacientes
      </button>

      {/* Header */}
      <div style={{ background: D.card, border: `1px solid ${D.bdr}`, borderRadius: 14, padding: "18px 22px", marginBottom: 16, backdropFilter: "blur(12px)" }}>
        <h2 style={{ margin: 0, fontSize: 20, color: "#e0f2fe", fontFamily: F.dm }}>{fullName}</h2>
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8, flexWrap: "wrap" }}>
          <span style={{ color: D.txM, fontSize: 13 }}>{pat.tipoDocumento}: {pat.numeroDocumento}</span>
          {pat.eps && <Bdg t={pat.eps} c="#8b5cf6" />}
          {pat.miembroAfectado && <Bdg t={pat.miembroAfectado} c="#0284c7" />}
          {islData && <Bdg t={`${islData.s} - ${islData.n}`} c={islData.c} />}
        </div>
      </div>

      {/* Sub-tabs */}
      <div style={{ display: "flex", gap: 4, marginBottom: 16 }}>
        {SUBTABS.map(t => (
          <button key={t.id} onClick={() => { setSubTab(t.id); setEditing(false); }}
            style={{ padding: "8px 16px", borderRadius: 8, border: `1px solid ${subTab === t.id ? D.ac : "rgba(100,116,139,0.12)"}`, background: subTab === t.id ? "rgba(45,212,191,0.1)" : "transparent", color: subTab === t.id ? D.ac : "#64748b", fontSize: 12, fontWeight: subTab === t.id ? 700 : 500, cursor: "pointer", fontFamily: F.b }}>
            {t.l}
          </button>
        ))}
      </div>

      {/* Tab: Datos */}
      {subTab === "datos" && (
        editing ? (
          <PatientForm
            user={user}
            initialData={pat}
            editMode
            onCancel={() => setEditing(false)}
            onSaved={() => setEditing(false)}
            onUpdate={handleUpdate}
          />
        ) : (
          <div>
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
              <button onClick={() => setEditing(true)}
                style={{ padding: "7px 16px", borderRadius: 7, border: `1px solid ${D.bdr}`, background: "transparent", color: D.ac, fontSize: 12, cursor: "pointer", fontFamily: F.b, fontWeight: 600 }}>
                Editar datos
              </button>
            </div>
            <PatientDataView patient={pat} />
          </div>
        )
      )}

      {/* Tab: Evoluciones */}
      {subTab === "evol" && <Evoluciones patientId={pat.id} user={user} patient={pat} />}

      {/* Tab: Fotos */}
      {subTab === "fotos" && <ArchivoFotografico patientId={pat.id} user={user} patient={pat} />}

      {/* Tab: Consentimiento */}
      {subTab === "consent" && <ConsentimientoInformado patientId={pat.id} patient={pat} user={user} />}

      {/* Tab: Dashboard */}
      {subTab === "dashboard" && <PatientDashboard patientId={pat.id} />}
    </div>
  );
}

function PatientDataView({ patient: p }) {
  const islData = p.estadioISL != null ? ISL[p.estadioISL] : null;
  const Section = ({ title, rows }) => (
    <div style={{ background: D.card, border: `1px solid ${D.bdr}`, borderRadius: 14, padding: "16px 20px", marginBottom: 10, backdropFilter: "blur(12px)" }}>
      <h4 style={{ margin: "0 0 10px", fontSize: 13, color: D.ac, fontFamily: F.b, fontWeight: 700 }}>{title}</h4>
      {rows.map(([label, val], i) => val ? (
        <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", borderBottom: "1px solid rgba(100,116,139,0.06)" }}>
          <span style={{ color: "#94a3b8", fontSize: 12 }}>{label}</span>
          <span style={{ color: "#e2e8f0", fontSize: 12, fontWeight: 600 }}>{val}</span>
        </div>
      ) : null)}
    </div>
  );

  return (
    <div>
      <Section title="Identificaci\u00f3n" rows={[
        ["Tipo documento", p.tipoDocumento],
        ["N\u00famero", p.numeroDocumento],
        ["Nombre", `${p.primerNombre} ${p.segundoNombre || ""}`],
        ["Apellidos", `${p.primerApellido} ${p.segundoApellido || ""}`],
        ["Fecha nacimiento", p.fechaNacimiento],
        ["Sexo", p.sexo],
      ]} />
      <Section title="Contacto" rows={[
        ["Direcci\u00f3n", p.direccion],
        ["Ciudad", p.ciudad],
        ["Departamento", p.departamento],
        ["Tel\u00e9fono", p.telefono],
        ["Email", p.email],
      ]} />
      <Section title="Aseguramiento" rows={[
        ["EPS", p.eps],
        ["Tipo afiliaci\u00f3n", p.tipoAfiliacion],
      ]} />
      <Section title="Contacto de Emergencia" rows={[
        ["Nombre", p.contactoEmergencia?.nombre],
        ["Parentesco", p.contactoEmergencia?.parentesco],
        ["Tel\u00e9fono", p.contactoEmergencia?.telefono],
      ]} />
      <Section title="Datos Cl\u00ednicos" rows={[
        ["Etiolog\u00eda", p.etiologia],
        ["Miembro afectado", p.miembroAfectado],
        ["Estadio ISL", islData ? `${islData.s} - ${islData.n}` : ""],
        ["Fecha diagn\u00f3stico", p.fechaDiagnostico],
        ["Antecedentes oncol\u00f3gicos", p.antecedentesOncologicos],
      ]} />
    </div>
  );
}
